'use client'

import React from 'react'
import {
  FileText,
  Shield,
  CheckCircle,
  XCircle,
  Code,
  AlertTriangle,
  Target,
  Zap,
  Users,
  BarChart3,
  GitBranch,
  RefreshCw,
  ArrowRight,
  Terminal,
  FileCode,
  Bug,
} from 'lucide-react'

/**
 * Claude Code Technical Debt Prevention Diagrams
 * Visual components for the Claude Code mitigation blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * CLAUDE.md Impact & Optimization Flow Diagram
 */
export function ClaudeMdImpactDiagram() {
  const steps = [
    { step: 1, label: 'Run on Tasks', icon: Terminal, color: '#008dcc' },
    { step: 2, label: 'Evaluate Results', icon: CheckCircle, color: '#FDC304' },
    { step: 3, label: 'Get LLM Feedback', icon: RefreshCw, color: '#8b5cf6' },
    { step: 4, label: 'Optimize CLAUDE.md', icon: FileText, color: '#F90B8A' },
    { step: 5, label: 'Re-run & Iterate', icon: Zap, color: '#10b981' },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          CLAUDE.md: YOUR MOST POWERFUL LEVER
        </h3>
        <p className='text-sm text-muted-foreground'>
          Prompt Learning achieves +5-10% improvement on SWE Bench without
          changing architecture
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        {/* Optimization cycle */}
        <div className='w-full'>
          <div className='flex flex-wrap items-center justify-center gap-2'>
            {steps.map((s, index, arr) => (
              <React.Fragment key={s.step}>
                <div className='flex flex-col items-center p-3 rounded-xl bg-background border border-border/60'>
                  <div
                    className='w-10 h-10 rounded-full flex items-center justify-center mb-2'
                    style={{ backgroundColor: `${s.color}20` }}
                  >
                    <s.icon className='w-5 h-5' style={{ color: s.color }} />
                  </div>
                  <span
                    className='text-[10px] font-bold'
                    style={{ color: s.color }}
                  >
                    STEP {s.step}
                  </span>
                  <span className='text-[10px] text-foreground text-center max-w-[70px]'>
                    {s.label}
                  </span>
                </div>
                {index < arr.length - 1 && (
                  <ArrowRight className='w-4 h-4 text-muted-foreground flex-shrink-0' />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Key stats */}
        <div className='grid grid-cols-3 gap-3 w-full max-w-lg'>
          <div className='p-3 rounded-xl bg-[#008dcc]/10 border border-[#008dcc]/30 text-center'>
            <p className='text-2xl font-bold text-[#008dcc]'>+5-10%</p>
            <p className='text-[10px] text-muted-foreground'>
              SWE Bench improvement
            </p>
          </div>
          <div className='p-3 rounded-xl bg-[#FDC304]/10 border border-[#FDC304]/30 text-center'>
            <p className='text-2xl font-bold text-[#FDC304]'>0</p>
            <p className='text-[10px] text-muted-foreground'>
              Architecture changes
            </p>
          </div>
          <div className='p-3 rounded-xl bg-[#10b981]/10 border border-[#10b981]/30 text-center'>
            <p className='text-2xl font-bold text-green-600 dark:text-green-400'>
              Codebase
            </p>
            <p className='text-[10px] text-muted-foreground'>Specific rules</p>
          </div>
        </div>

        {/* Key insight */}
        <div className='px-4 py-3 rounded-xl bg-[#008dcc]/10 border border-[#008dcc]/30'>
          <p className='text-xs md:text-sm text-center text-foreground'>
            <span className='font-semibold text-[#008dcc]'>
              Critical insight:
            </span>{' '}
            "Optimizing CLAUDE.md can be unexpectedly effective for your
            specific codebase" — rules that work for Pixelmojo may differ from
            generic recommendations.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Vulnerability Rates by Language Diagram
 */
export function VulnerabilityByLanguageDiagram() {
  const languages = [
    { name: 'Java', insecure: 71, secure: 29, color: '#ef4444' },
    { name: 'JavaScript', insecure: 43, secure: 57, color: '#f97316' },
    { name: 'Python', insecure: 38, secure: 62, color: '#FDC304' },
  ]

  const vulnerabilities = [
    { name: 'SQL Injection', secure: 80, color: '#10b981' },
    { name: 'Cross-Site Scripting', secure: 14, color: '#ef4444' },
    { name: 'Log Injection', secure: 12, color: '#ef4444' },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          AI CODE SECURITY BY LANGUAGE
        </h3>
        <p className='text-sm text-muted-foreground'>
          Veracode analysis of 100+ LLMs: Only 55% of AI-generated code is
          secure
        </p>
      </div>

      <div className='flex flex-col items-center gap-6'>
        {/* Language bars */}
        <div className='w-full max-w-md space-y-3'>
          <p className='text-xs font-semibold text-muted-foreground uppercase'>
            Insecure Code by Language
          </p>
          {languages.map(lang => (
            <div key={lang.name} className='space-y-1'>
              <div className='flex items-center justify-between text-xs'>
                <span className='font-medium text-foreground'>{lang.name}</span>
                <span className='font-bold' style={{ color: lang.color }}>
                  {lang.insecure}% insecure
                </span>
              </div>
              <div className='relative h-6 bg-muted rounded-lg overflow-hidden'>
                <div
                  className='absolute top-0 left-0 h-full rounded-lg flex items-center justify-end pr-2'
                  style={{
                    width: `${lang.insecure}%`,
                    backgroundColor: lang.color,
                  }}
                >
                  <span className='text-[10px] font-bold text-white'>
                    {lang.insecure}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Vulnerability-specific rates */}
        <div className='w-full max-w-md space-y-3'>
          <p className='text-xs font-semibold text-muted-foreground uppercase'>
            Secure Rate by Vulnerability Type
          </p>
          {vulnerabilities.map(vuln => (
            <div key={vuln.name} className='space-y-1'>
              <div className='flex items-center justify-between text-xs'>
                <span className='font-medium text-foreground'>{vuln.name}</span>
                <span
                  className='font-bold'
                  style={{ color: vuln.secure > 50 ? '#10b981' : '#ef4444' }}
                >
                  {vuln.secure}% secure
                </span>
              </div>
              <div className='relative h-6 bg-red-500/20 rounded-lg overflow-hidden'>
                <div
                  className='absolute top-0 left-0 h-full bg-green-500 rounded-lg flex items-center justify-end pr-2'
                  style={{ width: `${vuln.secure}%` }}
                >
                  {vuln.secure > 20 && (
                    <span className='text-[10px] font-bold text-white'>
                      {vuln.secure}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Warning callout */}
        <div className='w-full max-w-md p-3 rounded-xl bg-red-500/10 border border-red-500/30'>
          <div className='flex items-start gap-2'>
            <AlertTriangle className='w-5 h-5 text-red-500 flex-shrink-0 mt-0.5' />
            <div>
              <p className='text-sm font-bold text-foreground'>
                XSS: 86% Vulnerable!
              </p>
              <p className='text-xs text-muted-foreground'>
                Cross-Site Scripting is AI's biggest weakness. Explicit security
                constraints in prompts are essential.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Security Tool Capabilities Matrix
 */
export function SecurityToolMatrixDiagram() {
  const tools = ['Snyk', 'SonarQube', 'Qodo', 'GitGuardian']
  const capabilities = [
    { name: 'SQL Injection', values: [92, 80, 70, 0] },
    { name: 'XSS Detection', values: [85, 70, 65, 0] },
    { name: 'Code Quality/Debt', values: [30, 95, 90, 0] },
    { name: 'Dependency Vulns', values: [95, 0, 0, 0] },
    { name: 'Secrets Detection', values: [85, 75, 60, 98] },
  ]

  const getColor = (value: number) => {
    if (value === 0) return 'bg-muted text-muted-foreground'
    if (value >= 90) return 'bg-green-500 text-white'
    if (value >= 70) return 'bg-[#008dcc] text-white'
    if (value >= 50) return 'bg-[#FDC304] text-black'
    return 'bg-orange-500 text-white'
  }

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          SECURITY TOOL CAPABILITIES
        </h3>
        <p className='text-sm text-muted-foreground'>
          No single tool covers everything — layer defenses for comprehensive
          protection
        </p>
      </div>

      {/* Matrix */}
      <div className='overflow-x-auto'>
        <table className='w-full text-xs'>
          <thead>
            <tr>
              <th className='text-left p-2 text-muted-foreground'>
                Capability
              </th>
              {tools.map(tool => (
                <th
                  key={tool}
                  className='p-2 text-center text-foreground font-semibold'
                >
                  {tool}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {capabilities.map(cap => (
              <tr key={cap.name} className='border-t border-border/60'>
                <td className='p-2 text-foreground font-medium'>{cap.name}</td>
                {cap.values.map((value, idx) => (
                  <td key={idx} className='p-2 text-center'>
                    <span
                      className={`inline-block px-2 py-1 rounded text-[10px] font-bold ${getColor(value)}`}
                    >
                      {value === 0 ? '—' : `${value}%`}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className='mt-4 flex flex-wrap items-center justify-center gap-3 text-[10px]'>
        <div className='flex items-center gap-1'>
          <div className='w-3 h-3 rounded bg-green-500' />
          <span className='text-muted-foreground'>90%+</span>
        </div>
        <div className='flex items-center gap-1'>
          <div className='w-3 h-3 rounded bg-[#008dcc]' />
          <span className='text-muted-foreground'>70-89%</span>
        </div>
        <div className='flex items-center gap-1'>
          <div className='w-3 h-3 rounded bg-[#FDC304]' />
          <span className='text-muted-foreground'>50-69%</span>
        </div>
        <div className='flex items-center gap-1'>
          <div className='w-3 h-3 rounded bg-muted' />
          <span className='text-muted-foreground'>Not covered</span>
        </div>
      </div>

      {/* Key insight */}
      <div className='mt-4 px-4 py-3 rounded-xl bg-[#008dcc]/10 border border-[#008dcc]/30'>
        <p className='text-xs md:text-sm text-center text-foreground'>
          <span className='font-semibold text-[#008dcc]'>
            Defense in depth:
          </span>{' '}
          Combine Snyk (dependencies) + SonarQube (quality) + Qodo (AI review) +
          GitGuardian (secrets) for comprehensive coverage.
        </p>
      </div>
    </div>
  )
}

/**
 * Six Core Elements Framework Diagram
 */
export function SixElementsFrameworkDiagram() {
  const elements = [
    {
      name: 'Role/Persona',
      purpose: 'Set expertise level',
      example:
        '"Senior backend engineer specializing in HIPAA-compliant systems"',
      icon: Users,
      color: '#008dcc',
    },
    {
      name: 'Goal/Task',
      purpose: 'Define clear outcome',
      example: '"Implement patient data export API meeting HIPAA requirements"',
      icon: Target,
      color: '#FDC304',
    },
    {
      name: 'Context',
      purpose: 'Technical environment',
      example: '"Stack: Next.js + Prisma + PostgreSQL, deployed on Railway"',
      icon: Code,
      color: '#8b5cf6',
    },
    {
      name: 'Format',
      purpose: 'Output structure',
      example: '"TypeScript with JSDoc, Jest tests, OpenAPI spec"',
      icon: FileCode,
      color: '#F90B8A',
    },
    {
      name: 'Examples',
      purpose: 'Show desired patterns',
      example: 'Include similar implementations from your codebase',
      icon: FileText,
      color: '#10b981',
    },
    {
      name: 'Constraints',
      purpose: 'Set boundaries',
      example: '"Must include audit logging, encryption, RBAC"',
      icon: Shield,
      color: '#ef4444',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE SIX CORE ELEMENTS FRAMEWORK
        </h3>
        <p className='text-sm text-muted-foreground'>
          Every production prompt should include these components
        </p>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
        {elements.map(el => (
          <div
            key={el.name}
            className='p-3 rounded-xl bg-background border-l-4 border border-border/60'
            style={{ borderLeftColor: el.color }}
          >
            <div className='flex items-start gap-3'>
              <div
                className='p-2 rounded-lg flex-shrink-0'
                style={{ backgroundColor: `${el.color}20` }}
              >
                <el.icon className='w-4 h-4' style={{ color: el.color }} />
              </div>
              <div className='flex-1 min-w-0'>
                <h4 className='text-foreground font-semibold text-sm'>
                  {el.name}
                </h4>
                <p className='text-muted-foreground text-[10px] mb-1'>
                  {el.purpose}
                </p>
                <p
                  className='text-[10px] text-foreground italic truncate'
                  title={el.example}
                >
                  {el.example}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Key insight */}
      <div className='mt-4 px-4 py-3 rounded-xl bg-[#FDC304]/10 border border-[#FDC304]/30'>
        <p className='text-xs md:text-sm text-center text-foreground'>
          <span className='font-semibold text-[#FDC304]'>Pro tip:</span> Claude
          is fine-tuned to pay special attention to XML tags. Use{' '}
          {`<requirements>`}, {`<security_constraints>`},{' '}
          {`<technical_context>`} for structure.
        </p>
      </div>
    </div>
  )
}

/**
 * 90-Day Implementation Roadmap
 */
export function NinetyDayRoadmapDiagram() {
  const phases = [
    {
      phase: 'Foundation',
      days: '1-30',
      color: '#008dcc',
      weeks: [
        'Deploy CLAUDE.md with security + quality standards',
        'Set up CI/CD quality gates (Snyk, SonarQube)',
        'Implement pre-commit hooks, code tagging',
        'Establish baseline metrics (churn, TDR)',
      ],
    },
    {
      phase: 'Team Enablement',
      days: '31-60',
      color: '#FDC304',
      weeks: [
        'Security-first prompting training (OWASP)',
        'Code quality workshops (DRY, naming)',
        'Test-driven development with AI',
        'Production readiness (monitoring, logging)',
      ],
    },
    {
      phase: 'Optimization',
      days: '61-90',
      color: '#10b981',
      weeks: [
        'Analyze metrics, identify patterns',
        'Optimize CLAUDE.md based on failures',
        'Build prompt template library',
        'Implement feedback loops',
      ],
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          90-DAY IMPLEMENTATION ROADMAP
        </h3>
        <p className='text-sm text-muted-foreground'>
          Foundation → Team Enablement → Optimization
        </p>
      </div>

      {/* Progress bar */}
      <div className='w-full h-3 rounded-full bg-muted mb-6 overflow-hidden flex'>
        <div
          className='h-full'
          style={{ width: '33.33%', backgroundColor: '#008dcc' }}
        />
        <div
          className='h-full'
          style={{ width: '33.33%', backgroundColor: '#FDC304' }}
        />
        <div
          className='h-full'
          style={{ width: '33.33%', backgroundColor: '#10b981' }}
        />
      </div>

      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        {phases.map(phase => (
          <div
            key={phase.phase}
            className='p-4 rounded-xl bg-background border-t-4 border border-border/60'
            style={{ borderTopColor: phase.color }}
          >
            <div className='flex items-center gap-2 mb-3'>
              <div
                className='w-8 h-8 rounded-full flex items-center justify-center'
                style={{ backgroundColor: phase.color }}
              >
                <span className='text-white font-bold text-xs'>
                  {phase.phase === 'Foundation'
                    ? '1'
                    : phase.phase === 'Team Enablement'
                      ? '2'
                      : '3'}
                </span>
              </div>
              <div>
                <p className='font-bold text-sm text-foreground'>
                  {phase.phase}
                </p>
                <p className='text-[10px] text-muted-foreground'>
                  Days {phase.days}
                </p>
              </div>
            </div>
            <ul className='space-y-2'>
              {phase.weeks.map((week, i) => (
                <li key={i} className='flex items-start gap-2'>
                  <CheckCircle
                    className='w-3 h-3 mt-0.5 flex-shrink-0'
                    style={{ color: phase.color }}
                  />
                  <span className='text-[10px] text-muted-foreground'>
                    {week}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Key insight */}
      <div className='mt-4 px-4 py-3 rounded-xl bg-[#10b981]/10 border border-[#10b981]/30'>
        <p className='text-xs md:text-sm text-center text-foreground'>
          <span className='font-semibold text-green-600 dark:text-green-400'>
            Each phase builds on the previous.
          </span>{' '}
          Don't skip Foundation — guardrails must be in place before team
          training.
        </p>
      </div>
    </div>
  )
}

/**
 * Metrics Dashboard Comparison
 */
export function MetricsDashboardDiagram() {
  const metrics = [
    {
      name: 'Code Churn Rate',
      industry: '+41%',
      target: '<10%',
      icon: RefreshCw,
    },
    {
      name: 'Change Failure Rate',
      industry: 'Varies',
      target: '<5%',
      icon: Bug,
    },
    {
      name: 'Test Coverage',
      industry: '~60%',
      target: '>80%',
      icon: CheckCircle,
    },
    {
      name: 'Security Findings',
      industry: '45% have vulns',
      target: '0 high/critical',
      icon: Shield,
    },
    {
      name: 'PR Revert Rate',
      industry: '~5-10%',
      target: '<2%',
      icon: GitBranch,
    },
    {
      name: 'Technical Debt Ratio',
      industry: '5-20%',
      target: '<5%',
      icon: BarChart3,
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          METRICS DASHBOARD
        </h3>
        <p className='text-sm text-muted-foreground'>
          Track these weekly to measure Claude Code technical debt prevention
        </p>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
        {metrics.map(m => (
          <div
            key={m.name}
            className='p-3 rounded-xl bg-background border border-border/60'
          >
            <div className='flex items-center gap-2 mb-2'>
              <m.icon className='w-4 h-4 text-[#008dcc]' />
              <span className='text-sm font-semibold text-foreground'>
                {m.name}
              </span>
            </div>
            <div className='grid grid-cols-2 gap-2'>
              <div className='text-center p-2 rounded-lg bg-red-500/10'>
                <p className='text-[10px] text-muted-foreground uppercase'>
                  Industry
                </p>
                <p className='text-sm font-bold text-red-500'>{m.industry}</p>
              </div>
              <div className='text-center p-2 rounded-lg bg-green-500/10'>
                <p className='text-[10px] text-muted-foreground uppercase'>
                  Target
                </p>
                <p className='text-sm font-bold text-green-600 dark:text-green-400'>
                  {m.target}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Measurement tools */}
      <div className='mt-4 p-3 rounded-xl bg-muted'>
        <p className='text-xs font-semibold text-foreground mb-2'>
          Measurement Tools:
        </p>
        <div className='flex flex-wrap gap-2'>
          {['GitClear', 'DX Platform', 'CodeScene', 'SonarQube'].map(tool => (
            <span
              key={tool}
              className='text-[10px] px-2 py-1 rounded-full bg-background border border-border/60 text-muted-foreground'
            >
              {tool}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Prompt Comparison - Vague vs Explicit
 */
export function PromptComparisonDiagram() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          VAGUE vs EXPLICIT PROMPTING
        </h3>
        <p className='text-sm text-muted-foreground'>
          The quality of AI-generated code starts with how it is prompted
        </p>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        {/* Vague */}
        <div className='p-4 rounded-xl bg-red-500/10 border border-red-500/30'>
          <div className='flex items-center gap-2 mb-3'>
            <XCircle className='w-5 h-5 text-red-500' />
            <span className='font-bold text-sm text-red-500'>
              Vague (Generates Vulnerable Code)
            </span>
          </div>
          <div className='p-3 rounded-lg bg-background font-mono text-xs text-foreground'>
            "Create a login API"
          </div>
          <div className='mt-3 space-y-1'>
            <p className='text-[10px] text-red-500 flex items-center gap-1'>
              <XCircle className='w-3 h-3' /> No input validation
            </p>
            <p className='text-[10px] text-red-500 flex items-center gap-1'>
              <XCircle className='w-3 h-3' /> No rate limiting
            </p>
            <p className='text-[10px] text-red-500 flex items-center gap-1'>
              <XCircle className='w-3 h-3' /> No audit logging
            </p>
            <p className='text-[10px] text-red-500 flex items-center gap-1'>
              <XCircle className='w-3 h-3' /> Tokens in localStorage
            </p>
          </div>
        </div>

        {/* Explicit */}
        <div className='p-4 rounded-xl bg-green-500/10 border border-green-500/30'>
          <div className='flex items-center gap-2 mb-3'>
            <CheckCircle className='w-5 h-5 text-green-600 dark:text-green-400' />
            <span className='font-bold text-sm text-green-600 dark:text-green-400'>
              Explicit (Secure by Default)
            </span>
          </div>
          <div className='p-3 rounded-lg bg-background font-mono text-[10px] text-foreground max-h-32 overflow-y-auto'>
            Create login API with:
            <br />
            - Zod validation
            <br />
            - bcrypt (12 rounds)
            <br />
            - JWT 15min + refresh 7d
            <br />
            - httpOnly cookies
            <br />
            - Rate limit 5/15min
            <br />
            - Audit logging
            <br />- Parameterized queries
          </div>
          <div className='mt-3 space-y-1'>
            <p className='text-[10px] text-green-600 dark:text-green-400 flex items-center gap-1'>
              <CheckCircle className='w-3 h-3' /> Security constraints included
            </p>
            <p className='text-[10px] text-green-600 dark:text-green-400 flex items-center gap-1'>
              <CheckCircle className='w-3 h-3' /> Compliance requirements met
            </p>
          </div>
        </div>
      </div>

      {/* Key insight */}
      <div className='mt-4 px-4 py-3 rounded-xl bg-[#008dcc]/10 border border-[#008dcc]/30'>
        <p className='text-xs md:text-sm text-center text-foreground'>
          <span className='font-semibold text-[#008dcc]'>Key principle:</span>{' '}
          Include security requirements, controls, and context — not just
          functionality.
        </p>
      </div>
    </div>
  )
}
